import { createApi, type FetchBaseQueryError } from "@reduxjs/toolkit/query/react";
import { baseQueryWithAuth } from "@store/middleware";
import { setShopifyCartId } from "@store/slices/cartSlice";
import { userApi } from "@store/services/user";
import { type RootState } from "@store/rootReducer";
import { getApplicationSpaBackendUrl } from "@utils";
import { clearShopifyCartId, persistShopifyCartId } from "@hooks/useCart/useCart.storage";
import type { IShopifyCart, IShopifyLineItem, IShopifyProduct, IDocument, IPartner } from "@types";

// Inside onQueryStarted, `queryFulfilled` rejects with RTK Query's
// QueryFulfilledRejectionReason — `{ error, meta, isUnhandledError }`. Note the
// FetchBaseQueryError is nested under `.error`, unlike the top-level shape you
// get from `.unwrap()`. A 404 from the cart read means the remembered cart no
// longer exists — typically because it was checked out.
const isCartNotFoundError = (error: unknown): boolean =>
  (error as { error?: FetchBaseQueryError } | undefined)?.error?.status === 404;

export const SHOPIFY_API_REDUCER_KEY = "shopifyApi";
export const shopifyApi = createApi({
  reducerPath: SHOPIFY_API_REDUCER_KEY,
  baseQuery: (arguments_, api, extraOptions) =>
    baseQueryWithAuth({ baseUrl: getApplicationSpaBackendUrl() })(arguments_, api, {
      ...extraOptions,
      isExternalService: true,
    }),
  endpoints: (builder) => ({
    getProducts: builder.query<IShopifyProduct[], { partner?: IPartner["id"] }>({
      query: ({ partner }) => ({
        url: `/shopify/products${partner == null ? "" : `?${new URLSearchParams({ partner }).toString()}`}`,
        method: "GET",
      }),
    }),
    postCart: builder.mutation<
      IShopifyCart,
      {
        variantId: string;
        quantity?: number;
        externalDocumentIds: IDocument["id"][];
        discountCode?: string;
      }
    >({
      query: ({ variantId, quantity = 1, externalDocumentIds, discountCode }) => ({
        url: "/shopify/cart",
        method: "POST",
        body: {
          lines: [{ merchandiseId: variantId, quantity, externalDocumentIds }],
          // Only sent for campaign entries; the backend applies it to the new cart.
          ...(discountCode ? { discountCode } : {}),
        },
        headers: { "Content-Type": "application/json" },
      }),
      // Remember the created cart's id in the active user's slot and the store.
      async onQueryStarted(_, { dispatch, getState, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const userId = userApi.endpoints.getUserDetails.select()(getState() as RootState).data?.id;

          if (userId != null) {
            persistShopifyCartId(userId, data.cartId);
          }
          dispatch(setShopifyCartId(data.cartId));
          // Seed the getCart cache with the freshly created cart so useCart can
          // read it without a follow-up network request. See addCartLines.
          dispatch(shopifyApi.util.upsertQueryData("getCart", { cartId: data.cartId }, data));
        } catch {
          // Creation failed — nothing to persist.
        }
      },
    }),
    getCart: builder.query<IShopifyCart, { cartId: string }>({
      query: ({ cartId }) => ({
        url: `/shopify/cart/${encodeURIComponent(cartId)}`,
        method: "GET",
      }),
      // A 404 means the remembered cart no longer exists (e.g. it was checked
      // out). Silently drop the stale reference from storage and the store:
      // useCart skips the query once the id is null, so the cart reads as empty
      // instead of surfacing an error, and a later page load won't re-fetch the
      // dead cart from the per-user slot.
      async onQueryStarted(_, { dispatch, getState, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error) {
          if (isCartNotFoundError(error)) {
            const userId = userApi.endpoints.getUserDetails.select()(getState() as RootState).data?.id;

            if (userId != null) {
              clearShopifyCartId(userId);
            }
            dispatch(setShopifyCartId(null));
          }
        }
      },
    }),
    addCartLines: builder.mutation<
      IShopifyCart,
      {
        cartId: string;
        variantId: string;
        quantity?: number;
        externalDocumentIds: IDocument["id"][];
      }
    >({
      query: ({ cartId, variantId, quantity = 1, externalDocumentIds }) => ({
        url: `/shopify/cart/${encodeURIComponent(cartId)}/lines`,
        method: "POST",
        body: {
          lines: [{ merchandiseId: variantId, quantity, externalDocumentIds }],
        },
        headers: { "Content-Type": "application/json" },
      }),
      // Every cart endpoint returns the full, updated cart, so write the response
      // straight into the getCart cache instead of invalidating it. This keeps a
      // single source of truth and avoids a redundant getCart round-trip after
      // each mutation — getCart only hits the network when there's a known cart
      // id but no cached contents (i.e. a fresh page load).
      async onQueryStarted({ cartId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(shopifyApi.util.upsertQueryData("getCart", { cartId }, data));
        } catch {
          // Mutation failed — leave the cached cart untouched.
        }
      },
    }),
    removeLineItemFromCart: builder.mutation<
      IShopifyCart,
      { cartId: IShopifyCart["cartId"]; lineItemId: IShopifyLineItem["lineId"] }
    >({
      query: ({ cartId, lineItemId }) => ({
        url: `/shopify/cart/${encodeURIComponent(cartId)}/lines/${encodeURIComponent(lineItemId)}`,
        method: "DELETE",
      }),
      // Write the updated cart returned by the removal into the getCart cache so
      // the sidebar reflects it without a refetch. See addCartLines.
      async onQueryStarted({ cartId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(shopifyApi.util.upsertQueryData("getCart", { cartId }, data));
        } catch {
          // Removal failed — leave the cached cart untouched.
        }
      },
    }),
  }),
});

export const {
  useGetProductsQuery,
  useLazyGetProductsQuery,
  usePostCartMutation,
  useGetCartQuery,
  useLazyGetCartQuery,
  useAddCartLinesMutation,
  useRemoveLineItemFromCartMutation,
} = shopifyApi;
